import { create } from 'zustand'
import type { BlockSlot } from '@/core/time/blockGrid'
import type { Selection } from '@/core/services/markingService'
import type { ChangeSet } from '@/core/repository/entryRepository'
import type { Clipboard } from '@/core/services/clipboardService'
import { isEmptyChange, pushBounded } from '@/core/services/undoService'

/**
 * Transient timeline UI state.
 *
 * Persistent data lives in Dexie and is read with useLiveQuery. Nothing here
 * survives a reload, and nothing here is ever synced -- this is selection,
 * clipboard and undo history, not user data.
 *
 * The undo stacks live here rather than in a module singleton so the toolbar
 * buttons enable and disable reactively.
 */
/**
 * How long a freshly-marked block keeps its animation class.
 *
 * Comfortably longer than the slowest animation (200ms x the 2x speed
 * setting), so the class is never yanked mid-animation, and short enough that
 * re-marking the same block re-triggers it.
 */
const FLASH_MS = 600

let flashTimer: ReturnType<typeof setTimeout> | undefined

interface TimelineState {
  selection: Selection | null
  selectSlot: (slot: BlockSlot) => void
  extendSelection: (focus: BlockSlot) => void
  clearSelection: () => void
  setSelection: (selection: Selection | null) => void

  /**
   * Keeps the selection after marking instead of advancing the cursor, so a
   * range can be re-marked repeatedly. Set by the 280ms rung.
   */
  stickySelection: boolean
  setSticky: (sticky: boolean) => void

  clipboard: Clipboard | null
  setClipboard: (clipboard: Clipboard | null) => void

  undoStack: ChangeSet[]
  redoStack: ChangeSet[]
  /** Records an operation. Clears the redo stack, as any editor does. */
  recordChange: (change: ChangeSet) => void
  popUndo: () => ChangeSet | null
  popRedo: () => ChangeSet | null

  visibleDayKey: number | null
  setVisibleDayKey: (dayKey: number) => void

  hasUserScrolled: boolean
  markUserScrolled: () => void

  /**
   * Slots marked in the last instant, so they can play the mark animation
   * once and then stop.
   *
   * Lives in the store rather than in each row because the animation has to
   * be triggered by the WRITE, not by a render -- a row that re-renders for
   * any other reason (scroll, selection, a sibling changing) must not replay
   * it. Cleared on a timer rather than on animationend: several blocks are
   * marked at once and one shared timer is cheaper than N listeners.
   */
  justMarked: ReadonlySet<string>
  flashMarked: (keys: readonly string[]) => void
}

export const useTimelineStore = create<TimelineState>((set, get) => ({
  selection: null,

  selectSlot: (slot) => set({ selection: { anchor: slot, focus: slot } }),

  extendSelection: (focus) =>
    set((s) => {
      if (!s.selection) return s
      if (
        s.selection.focus.dayKey === focus.dayKey &&
        s.selection.focus.slotIndex === focus.slotIndex
      ) {
        return s
      }
      return { selection: { anchor: s.selection.anchor, focus } }
    }),

  clearSelection: () => set({ selection: null, stickySelection: false }),

  setSelection: (selection) => set({ selection }),

  stickySelection: false,
  setSticky: (stickySelection) => set({ stickySelection }),

  clipboard: null,
  setClipboard: (clipboard) => set({ clipboard }),

  undoStack: [],
  redoStack: [],

  recordChange: (change) => {
    if (isEmptyChange(change)) return
    set((s) => ({ undoStack: pushBounded(s.undoStack, change), redoStack: [] }))
  },

  popUndo: () => {
    const { undoStack } = get()
    const change = undoStack[undoStack.length - 1]
    if (!change) return null
    set((s) => ({
      undoStack: s.undoStack.slice(0, -1),
      redoStack: pushBounded(s.redoStack, change),
    }))
    return change
  },

  popRedo: () => {
    const { redoStack } = get()
    const change = redoStack[redoStack.length - 1]
    if (!change) return null
    set((s) => ({
      redoStack: s.redoStack.slice(0, -1),
      undoStack: pushBounded(s.undoStack, change),
    }))
    return change
  },

  visibleDayKey: null,
  setVisibleDayKey: (dayKey) =>
    set((s) => (s.visibleDayKey === dayKey ? s : { visibleDayKey: dayKey })),

  justMarked: new Set<string>(),

  flashMarked: (keys) => {
    if (keys.length === 0) return
    set({ justMarked: new Set(keys) })

    if (flashTimer !== undefined) clearTimeout(flashTimer)
    flashTimer = setTimeout(() => {
      // Replaced with a fresh empty Set, not mutated: subscribers compare by
      // reference and would never see a mutation.
      useTimelineStore.setState({ justMarked: new Set<string>() })
      flashTimer = undefined
    }, FLASH_MS)
  },

  hasUserScrolled: false,
  markUserScrolled: () => set((s) => (s.hasUserScrolled ? s : { hasUserScrolled: true })),
}))

/** Stable key for a slot, for Sets and React keys. */
export function slotKey(slot: BlockSlot): string {
  return `${slot.dayKey}:${slot.slotIndex}`
}
